// components/UserProfile/PhotoGallery.js
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Button,
  Box,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import UserProfileServices from "../../services/userProfile.services";
import logger from "../../utils/logger";
import styles from "./UserProfile.module.css";

/**
 * PhotoGallery component for managing multiple user photos
 * Allows users to view, select active photo, and delete photos
 */
const PhotoGallery = ({
  open,
  onClose,
  onPhotoSelected,
  onNotification,
  userProfile,
}) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [settingActive, setSettingActive] = useState(null);
  const [profileSettings, setProfileSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchPhotos();
      fetchProfileSettings();
    }
  }, [open]);

  // Add effect to refresh photos when userProfile changes (like after a deletion)
  useEffect(() => {
    if (open && userProfile) {
      fetchPhotos();
    }
  }, [userProfile, open]);

  const fetchProfileSettings = async () => {
    try {
      const settings = await UserProfileServices.getProfileSettings();
      setProfileSettings(settings.data);
    } catch (error) {
      logger.error("Error fetching profile settings:", error);
    }
  };

  const fetchPhotos = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await UserProfileServices.getUserPhotos();
      setPhotos(response.data || []);
    } catch (err) {
      logger.error("Error fetching photos:", err);
      setError("Failed to load photos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetActivePhoto = async (photoKey) => {
    setSettingActive(photoKey);
    try {
      const response = await UserProfileServices.setActivePhoto(photoKey);
      // Refresh photos to update UI
      await fetchPhotos();

      if (onPhotoSelected && response.profilePictureUrl) {
        onPhotoSelected(response.profilePictureUrl);
      }

      if (onNotification) {
        onNotification("Profile picture set successfully", "success");
      }
    } catch (error) {
      logger.error("Error setting active photo:", error);
      if (onNotification) {
        onNotification(
          "Failed to set profile picture. Please try again.",
          "error",
        );
      }
    } finally {
      setSettingActive(null);
    }
  };

  const handleUnsetActivePhoto = async () => {
    setSettingActive("unset");
    try {
      await UserProfileServices.unsetActivePhoto();
      await fetchPhotos();

      // Notify parent component that profile picture was removed
      if (onPhotoSelected) {
        onPhotoSelected(null);
      }

      if (onNotification) {
        onNotification("Profile picture removed successfully", "success");
      }
    } catch (error) {
      logger.error("Error removing active photo:", error);
      if (onNotification) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to remove profile picture. Please try again.";
        onNotification(errorMessage, "error");
      }
    } finally {
      setSettingActive(null);
    }
  };

  const handleDeletePhoto = async (photoKey) => {
    setDeletingPhoto(photoKey);
    try {
      // Check if the photo being deleted is currently active
      const photoToDelete = photos.find((photo) => photo.key === photoKey);
      const wasActivePhoto = photoToDelete?.isActive;
      await UserProfileServices.deleteSpecificPhoto(photoKey, true);
      setPhotos((prevPhotos) =>
        prevPhotos.filter((photo) => photo.key !== photoKey),
      );

      // For manual behavior: Only clear the profile picture if we explicitly deleted the active one
      // Do not automatically assign a new profile picture
      if (wasActivePhoto && onPhotoSelected) {
        onPhotoSelected(null); // Clear the active photo, user must manually select a new one
      }

      if (onNotification) {
        onNotification("Photo deleted successfully", "success");
      }
    } catch (error) {
      logger.error("Error deleting photo:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete photo";
      if (onNotification) {
        onNotification(errorMessage, "error");
      }
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      // Create a temporary link to download the image
      const link = document.createElement("a");
      link.href = photo.url;
      link.download = `profile-photo-${new Date(photo.uploadedAt).toISOString().split("T")[0]}.${photo.fileType.split("/")[1]}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (onNotification) {
        onNotification("Photo download started", "success");
      }
    } catch (error) {
      logger.error("Error downloading photo:", error);
      if (onNotification) {
        onNotification("Failed to download photo", "error");
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input
    event.target.value = "";

    // Check if at photo limit
    if (isAtPhotoLimit()) {
      if (onNotification) {
        onNotification(
          "You have reached the maximum number of photos allowed",
          "error",
        );
      }
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      if (onNotification) {
        onNotification("Please select a valid image file", "error");
      }
      return;
    }

    // Validate file size (e.g., 5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      if (onNotification) {
        onNotification("Image file size must be less than 5MB", "error");
      }
      return;
    }

    setUploading(true);
    try {
      // For PhotoGallery: Upload photo but don't set as active (setAsActive defaults to false)
      // Users must manually set photos as active using the "Set as Active" button
      await UserProfileServices.uploadProfilePicture(file);

      // Refresh photos list to include the new photo
      await fetchPhotos();

      if (onNotification) {
        onNotification("Photo uploaded successfully", "success");
      }
    } catch (error) {
      logger.error("Error uploading photo:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to upload photo";
      if (onNotification) {
        onNotification(errorMessage, "error");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getPhotoLimitText = () => {
    if (!profileSettings?.profilePicture) return "";
    const { maxPhotos } = profileSettings.profilePicture;
    return `${photos.length}/${maxPhotos} photos`;
  };

  const isAtPhotoLimit = () => {
    if (!profileSettings?.profilePicture) return false;
    return photos.length >= profileSettings.profilePicture.maxPhotos;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: styles.dialogPaper,
      }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Manage Photos</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Upload section */}
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="textSecondary">
            {getPhotoLimitText()}
          </Typography>
          <Button
            variant="contained"
            onClick={handleUploadClick}
            disabled={isAtPhotoLimit() || uploading}
            startIcon={
              uploading ? <CircularProgress size={20} /> : <AddAPhotoIcon />
            }
            sx={{
              backgroundColor: "#1d9f9f",
              "&:hover": { backgroundColor: "#168080" },
            }}
          >
            {uploading ? "Uploading..." : "Upload Photo"}
          </Button>
        </Box>

        {isAtPhotoLimit() && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You've reached the maximum number of photos. Delete some photos to
            upload new ones.
          </Alert>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/*"
          style={{ display: "none" }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button size="small" onClick={fetchPhotos} sx={{ ml: 1 }}>
              Try Again
            </Button>
          </Alert>
        ) : photos.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            textAlign="center"
          >
            <PhotoCameraIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No photos uploaded yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload your first photo to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo.key}>
                <Card className={styles.photoCard}>
                  <Box position="relative">
                    <CardMedia
                      component="img"
                      height="200"
                      image={photo.url}
                      alt="Profile photo"
                      className={styles.photoImage}
                    />

                    {/* Active photo indicator */}
                    {photo.isActive && (
                      <Box
                        position="absolute"
                        top={8}
                        left={8}
                        className={styles.activePhotoBadge}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 16, color: "white" }}
                        />
                        <span>ACTIVE</span>
                      </Box>
                    )}

                    {/* Photo actions */}
                    <Box
                      position="absolute"
                      top={8}
                      right={8}
                      className={styles.photoActions}
                    >
                      <Tooltip title="Download photo">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPhoto(photo)}
                          className={styles.photoActionButton}
                          style={{ marginRight: "4px" }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete photo">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePhoto(photo.key)}
                            disabled={deletingPhoto === photo.key}
                            className={styles.photoActionButton}
                          >
                            {deletingPhoto === photo.key ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Photo info and actions */}
                  <Box p={1}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Uploaded:{" "}
                      {new Date(photo.uploadedAt).toLocaleDateString()}
                    </Typography>

                    {!photo.isActive && (
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => handleSetActivePhoto(photo.key)}
                        disabled={settingActive === photo.key}
                        sx={{
                          mt: 1,
                          borderColor: "#1d9f9f",
                          color: "#1d9f9f",
                          "&:hover": {
                            borderColor: "#168080",
                            backgroundColor: "rgba(29, 159, 159, 0.04)",
                          },
                        }}
                      >
                        {settingActive === photo.key ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Set as Active"
                        )}
                      </Button>
                    )}

                    {photo.isActive && (
                      <Box sx={{ textAlign: "center", mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          onClick={handleUnsetActivePhoto}
                          disabled={settingActive === "unset"}
                          sx={{
                            borderColor: "#d32f2f",
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            "&:hover": {
                              borderColor: "#c62828",
                              backgroundColor: "rgba(211, 47, 47, 0.04)",
                            },
                          }}
                        >
                          {settingActive === "unset" ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Remove as Profile Picture"
                          )}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#1d9f9f",
            color: "#1d9f9f",
            "&:hover": {
              borderColor: "#168080",
              backgroundColor: "rgba(29, 159, 159, 0.04)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoGallery;
